import React, { useEffect, useState } from 'react';
import { XCircle, CheckCircle, Info, X } from 'lucide-react'; // Icons for different toast types

/**
 * Individual Toast Message Component.
 * Displays a notification with different types (success, error, info) and a close button.
 * Styled with Tailwind CSS, adhering to the Gmail color scheme.
 *
 * @param {object} props - Component props.
 * @param {string} props.id - Unique ID for the toast.
 * @param {string} props.message - The message to display.
 * @param {'success' | 'error' | 'info'} props.type - Type of toast for styling.
 * @param {function} props.onClose - Callback function when the toast is closed.
 */
const Toast = ({ id, message, type, onClose }) => {
    const [isVisible, setIsVisible] = useState(true);

    // Determine styles based on toast type
    const typeStyles = {
        success: 'bg-green-100 border-green-400 text-green-800',
        error: 'bg-red-100 border-red-400 text-red-800',
        info: 'bg-blue-100 border-blue-400 text-blue-800',
    };

    const iconMap = {
        success: <CheckCircle size={20} className="text-green-600" />,
        error: <XCircle size={20} className="text-red-600" />,
        info: <Info size={20} className="text-blue-600" />,
    };

    const handleClose = () => {
        setIsVisible(false);
        // Allow time for fade-out animation before removing from DOM
        setTimeout(() => onClose(id), 300);
    };

    // Animation classes
    const animationClasses = isVisible
        ? 'opacity-100 translate-x-0'
        : 'opacity-0 translate-x-full';

    return (
        <div
            className={`flex items-center justify-between p-4 mb-3 rounded-lg shadow-md border
                        transition-all duration-300 ease-in-out transform
                        ${typeStyles[type]} ${animationClasses}`}
            role="alert"
        >
            <div className="flex items-center">
                <span className="mr-3">{iconMap[type]}</span>
                <p className="text-sm font-medium">{message}</p>
            </div>
            <button
                onClick={handleClose}
                className={`ml-4 p-1 rounded-full transition-colors duration-200
                            ${type === 'success' ? 'hover:bg-green-200 text-green-700' : ''}
                            ${type === 'error' ? 'hover:bg-red-200 text-red-700' : ''}
                            ${type === 'info' ? 'hover:bg-blue-200 text-blue-700' : ''}
                            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current`}
                aria-label="Close"
            >
                <X size={16} />
            </button>
        </div>
    );
};

export default Toast;
